// src/components/affiliate/AffiliateDashboard.js
import React, { useEffect, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

// Firebase
import { auth, db, analytics } from '../../firebase/firebase';
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail
} from 'firebase/auth';
import {
  collection, query, where, getDocs, doc, getDoc, getCountFromServer
} from 'firebase/firestore';
import { logEvent } from 'firebase/analytics';

// UI
import AuthModal from '../auth/AuthModal';
import { UsersRound, Link as LinkIcon, RefreshCw } from 'lucide-react';

const googleProvider = new GoogleAuthProvider();

const money = (n) => {
  const v = Number(n || 0);
  return Number.isFinite(v) ? `$${v.toFixed(2)}` : '$0.00';
};

const dateOnly = (ts) => {
  try {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString();
  } catch { return '—'; }
};

// Mask like: joel2024@gmail.com -> j*****4@gmail.com
const maskEmail = (email) => {
  if (!email || typeof email !== 'string') return '(no email)';
  const [local, domain] = email.split('@');
  if (!domain) return '(no email)';
  if (!local || local.length <= 2) return `${local[0] || '*'}*****@${domain}`;
  const first = local[0];
  const last = local[local.length - 1];
  return `${first}*****${last}@${domain}`;
};

// Normalize sale doc fields (supports both legacy & new shapes)
const readSaleFields = (d) => {
  const sale = Number(d.totalSalePrice ?? d.totalSale ?? 0) || 0;
  const tpt = Number.isFinite(d.tptCommission) ? Number(d.tptCommission) : (sale * 0.20);
  const net = Number.isFinite(d.netAfterTpt) ? Number(d.netAfterTpt)
           : Number.isFinite(d.netAfterTPT) ? Number(d.netAfterTPT)
           : (sale - sale * 0.20);
  const rate = Number(d.commissionRate ?? d.commissionRateOverride ?? 0.20) || 0;
  const commission = Number.isFinite(d.affiliateCommission) ? Number(d.affiliateCommission)
                    : Number.isFinite(d.commissionDue) ? Number(d.commissionDue)
                    : (net * rate);
  const paid = !!d.paid;
  const userId = d.userId || null;
  const approvedAt = d.approvedAt || null;
  const userEmail = d.userEmail || null;
  return { sale, tpt, net, rate, commission, paid, userId, approvedAt, userEmail };
};

const isSalePurchased = (d) => {
  if (!d) return false;
  const sale = Number(d.totalSale ?? d.totalSalePrice ?? 0) || 0;
  const commission = Number(d.affiliateCommission ?? d.commissionDue ?? 0) || 0;
  return !!(d.paid || d.approvedAt || sale > 0 || commission > 0);
};

export default function AffiliateDashboard() {
  const [currentUser, setCurrentUser] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);

  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [linksByAffiliate, setLinksByAffiliate] = useState({});
  const [clicksByAffiliate, setClicksByAffiliate] = useState({});
  const [signupsByAffiliate, setSignupsByAffiliate] = useState({});
  const [rowsByAffiliate, setRowsByAffiliate] = useState({});
  const [commissionsByAffiliate, setCommissionsByAffiliate] = useState({});

  const [readError, setReadError] = useState(null);

  // --- Auth modal state (same wiring as LandingPage) ---
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tptStoreUrlInput, setTptStoreUrlInput] = useState('');
  const [authError, setAuthError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');

  const openAuthModal = (mode = 'signup') => {
    setAuthError('');
    setResetMessage('');
    setShowForgotPassword(false);
    setAuthMode(mode);
    setIsAuthModalOpen(true);
  };

  const handleAuthAction = async (mode) => {
    setAuthError('');
    try {
      if (mode === 'signup') {
        await createUserWithEmailAndPassword(auth, email, password);
        logEvent(analytics, 'sign_up', { method: 'Email', source: 'AffiliateDashboard' });
      } else {
        await signInWithEmailAndPassword(auth, email, password);
        logEvent(analytics, 'login', { method: 'Email', source: 'AffiliateDashboard' });
      }
      // Refresh claims (so affiliateIds custom claim is up-to-date) and close
      await auth.currentUser?.getIdToken(true);
      setIsAuthModalOpen(false);
    } catch (error) {
      setAuthError(String(error.message || error).replace('Firebase: ', ''));
    }
  };

  const handleGoogleSignIn = async () => {
    setAuthError('');
    try {
      await signInWithPopup(auth, googleProvider);
      logEvent(analytics, 'sign_up', { method: 'Google', source: 'AffiliateDashboard' });
      await auth.currentUser?.getIdToken(true);
      setIsAuthModalOpen(false);
    } catch (error) {
      setAuthError(String(error.message || error).replace('Firebase: ', ''));
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setAuthError('');
    setResetMessage('');
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetMessage('Password reset email sent! Please check your inbox.');
    } catch (error) {
      setAuthError(String(error.message || error).replace('Firebase: ', ''));
    }
  };

  useEffect(() => {
    const unsub = auth.onAuthStateChanged((u) => {
      setCurrentUser(u || null);
      setCheckingAuth(false);
    });
    return () => unsub();
  }, []);

  const loadAffiliates = useCallback(async (uid) => {
    setLoading(true);
    setReadError(null);
    try {
      // refresh token to pick up affiliateIds claims
      const tokenResult = await auth.currentUser?.getIdTokenResult(true).catch(() => null);
      const claimIds = (tokenResult?.claims?.affiliateIds && Array.isArray(tokenResult.claims.affiliateIds))
        ? tokenResult.claims.affiliateIds
        : [];

      let list = [];

      // Preferred: read by IDs in claims
      if (claimIds.length > 0) {
        const docsArr = await Promise.all(claimIds.map(async (id) => {
          try {
            const snap = await getDoc(doc(db, 'affiliates', id));
            return snap.exists() ? ({ id: snap.id, ...snap.data() }) : null;
          } catch {
            return null;
          }
        }));
        list = docsArr.filter(Boolean);
      }

      // Fallback: query by ownersUids
      if (list.length === 0) {
        try {
          const qAff = query(collection(db, 'affiliates'), where('ownersUids', 'array-contains', uid));
          const snap = await getDocs(qAff);
          list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        } catch (e) {
          setReadError(e?.code === 'permission-denied' ? 'permission-denied' : 'other');
        }
      }

      setAffiliates(list);

      const linkMap = {};
      const clickMap = {};
      const signupCountMap = {};
      const rowsMap = {};
      const commissionMap = {};

      await Promise.all(list.map(async (aff) => {
        // LINKS + total clicks + per-link signups/purchases
        try {
          const linksSnap = await getDocs(collection(db, 'affiliates', aff.id, 'links'));
          const linksRaw = linksSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          clickMap[aff.id] = linksRaw.reduce((sum, l) => sum + Number(l.clicks || 0), 0);

          const augmented = await Promise.all(
            linksRaw.map(async (lnk) => {
              // signups per link
              const attQ = query(
                collection(db, 'referralAttributions'),
                where('affiliateId', '==', aff.id),
                where('linkId', '==', lnk.id)
              );
              const countSnap = await getCountFromServer(attQ);
              const signupsForLink = countSnap.data().count || 0;

              // purchases per link (users with sale rows)
              let purchasesForLink = 0;
              if (signupsForLink > 0) {
                const attDocs = await getDocs(attQ);
                const userIds = [...new Set(attDocs.docs.map(d => d.data()?.userId).filter(Boolean))];
                if (userIds.length) {
                  const saleDocs = await Promise.all(userIds.map(uid => getDoc(doc(db, 'affiliates', aff.id, 'sales', uid))));
                  purchasesForLink = saleDocs.reduce((acc, s) => acc + (s.exists() && isSalePurchased(s.data()) ? 1 : 0), 0);
                }
              }

              return { ...lnk, _signups: signupsForLink, _purchases: purchasesForLink };
            })
          );

          linkMap[aff.id] = augmented;
        } catch {
          linkMap[aff.id] = [];
          clickMap[aff.id] = 0;
        }

        // ATTRIBUTIONS
        let attrRows = [];
        try {
          const attsSnap = await getDocs(
            query(collection(db, 'referralAttributions'), where('affiliateId', '==', aff.id))
          );
          signupCountMap[aff.id] = attsSnap.size || 0;

          attrRows = attsSnap.docs.map(d => {
            const data = d.data() || {};
            const uidRef = data.userId || d.id;
            return {
              uid: uidRef,
              emailMasked: maskEmail(data.userEmail || ''),
              status: data.purchaseCounted ? 'Purchased' : 'Trial',
              signedUpAt: data.attributedAt || data.createdAt || null,
              approvedAt: null,
            };
          });
        } catch {
          signupCountMap[aff.id] = 0;
          attrRows = [];
        }

        // SALES + commissions
        let paid = 0, due = 0, total = 0;
        const salesByUser = {};
        try {
          const salesSnap = await getDocs(collection(db, 'affiliates', aff.id, 'sales'));
          salesSnap.forEach(s => {
            const f = readSaleFields(s.data() || {});
            total += f.commission;
            if (f.paid) paid += f.commission; else due += f.commission;
            const key = f.userId || s.id;
            salesByUser[key] = f;
          });
        } catch { /* ignore */ }
        commissionMap[aff.id] = { commissionPaid: paid, commissionDue: due, commissionTotal: total };

        // MERGE rows
        const merged = [];
        const seen = new Set();

        for (const r of attrRows) {
          const sale = salesByUser[r.uid];
          const purchased = sale?.approvedAt || sale?.paid || (sale && sale.commission > 0);
          merged.push({
            uid: r.uid,
            emailMasked: r.emailMasked,
            status: purchased ? 'Purchased' : r.status,
            signedUpAt: r.signedUpAt,
            approvedAt: sale?.approvedAt || null,
            totalSale: sale?.sale ?? 0,
            tptFee: sale?.tpt ?? (sale ? sale.sale * 0.20 : 0),
            netAfterTPT: sale?.net ?? (sale ? (sale.sale - sale.sale * 0.20) : 0),
            rate: Number.isFinite(sale?.rate) ? sale.rate : 0.20,
            commission: sale?.commission ?? 0,
            paid: !!sale?.paid,
          });
          seen.add(r.uid);
        }

        Object.keys(salesByUser).forEach(uidKey => {
          if (seen.has(uidKey)) return;
          const sale = salesByUser[uidKey];
          merged.push({
            uid: uidKey,
            emailMasked: maskEmail(sale.userEmail || ''),
            status: sale.approvedAt || sale.paid ? 'Purchased' : 'Trial',
            signedUpAt: null,
            approvedAt: sale.approvedAt || null,
            totalSale: sale.sale,
            tptFee: sale.tpt,
            netAfterTPT: sale.net,
            rate: sale.rate,
            commission: sale.commission,
            paid: sale.paid,
          });
        });

        merged.sort((a, b) => {
          const ta = a.signedUpAt?.toMillis ? a.signedUpAt.toMillis() : (a.signedUpAt ? new Date(a.signedUpAt).getTime() : 0);
          const tb = b.signedUpAt?.toMillis ? b.signedUpAt.toMillis() : (b.signedUpAt ? new Date(b.signedUpAt).getTime() : 0);
          if (tb !== ta) return tb - ta;
          const aa = a.approvedAt?.toMillis ? a.approvedAt.toMillis() : (a.approvedAt ? new Date(a.approvedAt).getTime() : 0);
          const ab = b.approvedAt?.toMillis ? b.approvedAt.toMillis() : (b.approvedAt ? new Date(b.approvedAt).getTime() : 0);
          return ab - aa;
        });

        rowsMap[aff.id] = merged;
      }));

      setLinksByAffiliate(linkMap);
      setClicksByAffiliate(clickMap);
      setSignupsByAffiliate(signupCountMap);
      setRowsByAffiliate(rowsMap);
      setCommissionsByAffiliate(commissionMap);
    } catch (e) {
      console.error('Affiliate load failed:', e);
      setReadError(e?.code === 'permission-denied' ? 'permission-denied' : 'other');
      setAffiliates([]);
      setLinksByAffiliate({});
      setClicksByAffiliate({});
      setSignupsByAffiliate({});
      setRowsByAffiliate({});
      setCommissionsByAffiliate({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (currentUser?.uid) loadAffiliates(currentUser.uid);
  }, [currentUser, loadAffiliates]);

  const refresh = async () => {
    if (!currentUser?.uid) return;
    setRefreshing(true);
    await loadAffiliates(currentUser.uid);
    setRefreshing(false);
  };

  const copy = (text) => navigator.clipboard.writeText(text);

  if (checkingAuth) {
    return <div className="flex items-center justify-center h-screen">Checking session…</div>;
  }

  // --- Not signed in: show inline login/signup CTA + modal ---
  if (!currentUser) {
    return (
      <>
        <Helmet>
          <title>Affiliate Dashboard</title>
          <meta name="robots" content="index, follow" />
        </Helmet>

        <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6">
          <h1 className="text-2xl font-bold text-slate-900">Affiliate Dashboard</h1>
          <p className="mt-2 text-slate-600 text-center max-w-md">
            Sign in to view affiliates, tracking links, and referred users.
          </p>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={() => { logEvent(analytics, 'open_auth_modal', { mode: 'login', source: 'AffiliateDashboard' }); openAuthModal('login'); }}
              className="px-4 py-2 rounded-full bg-slate-900 text-white hover:bg-slate-800"
            >
              Log In
            </button>
            <button
              onClick={() => { logEvent(analytics, 'open_auth_modal', { mode: 'signup', source: 'AffiliateDashboard' }); openAuthModal('signup'); }}
              className="px-4 py-2 rounded-full bg-sky-600 text-white hover:bg-sky-700"
            >
              Sign Up Free
            </button>
            <a
              href="https://mrjoelstptgenerator.com/app"
              className="px-4 py-2 rounded-full bg-white border text-slate-700 hover:bg-slate-100"
            >
              Go to App
            </a>
          </div>

          <p className="mt-4 text-sm text-slate-500">
            Want to become an affiliate?{' '}
            <a className="text-sky-700 underline" href="mailto:info@mrjoelstptgenerator.com">
              info@mrjoelstptgenerator.com
            </a>
          </p>
        </div>

        {isAuthModalOpen && (
          <AuthModal
            onClose={() => setIsAuthModalOpen(false)}
            authMode={authMode}
            setAuthMode={setAuthMode}
            email={email}
            setEmail={setEmail}
            password={password}
            setPassword={setPassword}
            authError={authError}
            setAuthError={setAuthError}
            showForgotPassword={showForgotPassword}
            setShowForgotPassword={setShowForgotPassword}
            resetEmail={resetEmail}
            setResetEmail={setResetEmail}
            resetMessage={resetMessage}
            setResetMessage={setResetMessage}
            tptStoreUrlInput={tptStoreUrlInput}
            setTptStoreUrlInput={setTptStoreUrlInput}
            handleAuthAction={handleAuthAction}
            handlePasswordReset={handlePasswordReset}
            handleGoogleSignIn={handleGoogleSignIn}
          />
        )}
      </>
    );
  }

  // --- Signed in UI ---
  return (
    <>
      <Helmet>
        <title>Affiliate Dashboard</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-slate-100">
        {/* Header */}
        <header className="bg-white shadow-sm">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
            <Link to="/" className="text-lg font-bold text-sky-800">Mr Joel&apos;s Seller Tools</Link>
            <div className="flex items-center gap-2">
              <a
                href="https://mrjoelstptgenerator.com/app"
                className="px-3 py-1.5 rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200 border"
              >
                &larr; Back to App
              </a>
              <span className="text-sm text-slate-600">{currentUser.email}</span>
              <button
                onClick={refresh}
                className="p-2 rounded-md bg-slate-100 text-slate-700 hover:text-slate-900"
                title="Refresh"
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-2">
            <UsersRound className="w-7 h-7 text-sky-600" />
            <h1 className="text-2xl font-semibold text-slate-900">Affiliate Dashboard</h1>
          </div>

          <div className="mt-2 text-sm text-slate-600 text-center sm:text-left">
            Want to become an affiliate? Email{' '}
            <a className="text-sky-700 underline" href="mailto:info@mrjoelstptgenerator.com">
              info@mrjoelstptgenerator.com
            </a>.
          </div>

          {readError === 'permission-denied' && (
            <div className="mt-4 p-3 rounded-md border border-amber-300 bg-amber-50 text-amber-800 text-sm">
              We couldn’t read some affiliate data due to permissions. If you believe you should have access,
              please contact <a className="underline" href="mailto:info@mrjoelstptgenerator.com">info@mrjoelstptgenerator.com</a>.
            </div>
          )}

          {loading ? (
            <div className="mt-8 p-6 bg-white border rounded-lg">Loading your affiliates…</div>
          ) : affiliates.length === 0 ? (
            <div className="mt-8 p-6 bg-white border rounded-lg">
              <h2 className="text-lg font-semibold text-slate-800">No affiliate access found</h2>
              <p className="text-slate-600 mt-1">
                Your account isn’t listed as an owner on any affiliate yet. Ask the admin to add your email
                in the Admin page → Affiliate Management → Owners, or contact{' '}
                <a className="text-sky-700 underline" href="mailto:info@mrjoelstptgenerator.com">info@mrjoelstptgenerator.com</a>.
              </p>
            </div>
          ) : (
            <div className="mt-6 space-y-8">
              {affiliates.map((aff) => {
                const links = linksByAffiliate[aff.id] || [];
                const clicks = clicksByAffiliate[aff.id] || 0;
                const signups = signupsByAffiliate[aff.id] || 0;
                const commissions = commissionsByAffiliate[aff.id] || { commissionPaid: 0, commissionDue: 0, commissionTotal: 0 };
                const rows = rowsByAffiliate[aff.id] || [];

                return (
                  <div key={aff.id} className="bg-white border rounded-lg p-5">
                    {/* Header metrics */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="p-4 rounded-md border bg-slate-50 text-center">
                        <div className="text-sm text-slate-500">Affiliate</div>
                        <div className="text-lg font-semibold text-slate-900">{aff.name}</div>
                        <div className="text-[11px] text-slate-500 font-mono mt-1">{aff.id}</div>
                      </div>
                      <div className="p-4 rounded-md border bg-slate-50 text-center">
                        <div className="text-sm text-slate-500">Total Clicks</div>
                        <div className="text-2xl font-semibold text-slate-900">{clicks}</div>
                        <div className="mt-2 text-sm text-slate-500">
                          Signups: <span className="font-semibold text-slate-800">{signups}</span>
                          <span className="mx-2 text-slate-400">|</span>
                          Purchased:{' '}
                          <span className="font-semibold text-slate-800">
                            {typeof aff?.totals?.purchases === 'number' ? aff.totals.purchases : rows.filter(r => r.status === 'Purchased').length}
                          </span>
                        </div>
                      </div>
                      <div className="p-4 rounded-md border bg-slate-50 text-center">
                        <div className="text-sm text-slate-500">Commission</div>
                        <div className="text-slate-900 font-semibold">Paid: {money(commissions.commissionPaid)}</div>
                        <div className="text-slate-900 font-semibold">Due: {money(commissions.commissionDue)}</div>
                      </div>
                      <div className="p-4 rounded-md border bg-slate-50 text-center">
                        <div className="text-sm text-slate-500">Commission Total</div>
                        <div className="text-2xl font-semibold text-slate-900">{money(commissions.commissionTotal)}</div>
                      </div>
                    </div>

                    {/* Links */}
                    <div className="mt-6">
                      <h3 className="text-sm font-semibold text-slate-700 flex items-center justify-center sm:justify-start gap-2">
                        <LinkIcon className="w-4 h-4 text-sky-600" /> Your Tracking Links
                      </h3>
                      {links.length === 0 ? (
                        <p className="text-sm text-slate-500 mt-2 text-center">No links yet. Ask the admin to generate one for you.</p>
                      ) : (
                        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                          {links.map(link => (
                            <div key={link.id} className="p-3 bg-slate-50 border rounded-md">
                              <div className="text-xs text-slate-500 text-center">
                                Campaign: <span className="font-semibold">{link.campaign || 'general'}</span>
                                <span className="mx-2 text-slate-400">|</span>
                                Clicks: <span className="font-semibold">{link.clicks || 0}</span>
                                <span className="mx-2 text-slate-400">|</span>
                                Signups: <span className="font-semibold">{link._signups ?? 0}</span>
                                <span className="mx-2 text-slate-400">|</span>
                                Purchases: <span className="font-semibold">{link._purchases ?? 0}</span>
                              </div>
                              <div className="mt-1 text-[13px] font-mono break-all text-center">{link.publicUrl}</div>
                              <div className="mt-2 flex items-center justify-center gap-2">
                                <button
                                  onClick={() => copy(link.publicUrl)}
                                  className="px-2 py-1 text-xs rounded-md bg-white border hover:bg-slate-50"
                                >
                                  Copy
                                </button>
                                <a
                                  href={link.publicUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="px-2 py-1 text-xs rounded-md bg-sky-600 text-white hover:bg-sky-700"
                                >
                                  Open
                                </a>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Referred users table */}
                    <div className="mt-6 overflow-x-auto">
                      <table className="min-w-full divide-y divide-slate-200 table-fixed">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-3 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider text-center w-64">User Email</th>
                            <th className="px-3 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider text-center w-28">Status</th>
                            <th className="px-3 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider text-center w-36">Signed Up</th>
                            <th className="px-3 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider text-center w-48">Purchase Confirmed</th>
                            <th className="px-3 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider text-center w-28">Total Sale</th>
                            <th className="px-3 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider text-center w-28">TPT 20%</th>
                            <th className="px-3 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider text-center w-32">Net After TPT</th>
                            <th className="px-3 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider text-center w-20">Rate</th>
                            <th className="px-3 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider text-center w-32">Commission</th>
                            <th className="px-3 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider text-center w-28">Commission Paid</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-slate-200">
                          {(rowsByAffiliate[aff.id]?.length ?? 0) === 0 ? (
                            <tr>
                              <td className="px-3 py-3 text-sm text-slate-500 text-center" colSpan={10}>
                                No signups yet.
                              </td>
                            </tr>
                          ) : rows.map(r => (
                            <tr key={r.uid}>
                              <td className="px-3 py-2 text-sm text-slate-800 text-center">{r.emailMasked}</td>
                              <td className="px-3 py-2 text-sm text-center">
                                {r.status === 'Purchased' ? (
                                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                    Purchased
                                  </span>
                                ) : (
                                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                    Trial
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-sm text-center">{dateOnly(r.signedUpAt)}</td>
                              <td className="px-3 py-2 text-sm text-center">{dateOnly(r.approvedAt)}</td>
                              <td className="px-3 py-2 text-sm text-center">{money(r.totalSale)}</td>
                              <td className="px-3 py-2 text-sm text-center">{money(r.tptFee)}</td>
                              <td className="px-3 py-2 text-sm text-center">{money(r.netAfterTPT)}</td>
                              <td className="px-3 py-2 text-sm text-center">{Number(r.rate || 0).toFixed(2)}</td>
                              <td className="px-3 py-2 text-sm text-center font-semibold">{money(r.commission)}</td>
                              <td className="px-3 py-2 text-sm text-center">{r.paid ? 'YES' : 'NO'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>

      {/* Modal lives here so it can also be opened while signed in (if needed) */}
      {isAuthModalOpen && (
        <AuthModal
          onClose={() => setIsAuthModalOpen(false)}
          authMode={authMode}
          setAuthMode={setAuthMode}
          email={email}
          setEmail={setEmail}
          password={password}
          setPassword={setPassword}
          authError={authError}
          setAuthError={setAuthError}
          showForgotPassword={showForgotPassword}
          setShowForgotPassword={setShowForgotPassword}
          resetEmail={resetEmail}
          setResetEmail={setResetEmail}
          resetMessage={resetMessage}
          setResetMessage={setResetMessage}
          tptStoreUrlInput={tptStoreUrlInput}
          setTptStoreUrlInput={setTptStoreUrlInput}
          handleAuthAction={handleAuthAction}
          handlePasswordReset={handlePasswordReset}
          handleGoogleSignIn={handleGoogleSignIn}
        />
      )}
    </>
  );
}
